import React, { useCallback, useContext, useMemo } from 'react'
import { Box, Grid, Hidden } from '@hermes/web-components'
import { useRouter } from 'next/router'
import useTranslateMessage from '../../../../../hooks/useTranslateMessage'
import { AppDataContext } from '../../../../../providers/AppData'
import { SearchProfilesPageContext } from '../../../../../providers/SearchProfilesPage'
import { useGetSearchProfileData } from '../../../../../hooks/useGetProfileData'
import { PracticeOrSpecialistProfile } from '../../../../../types'
import { useGetUnitsByLocale } from '../../../../../hooks/useGetUnitsByLocale'
import useStatistic from '../../../../../hooks/useStatistic'
import { AppointmentType, FilterType } from '../../../../../constants/search'
import {
  getAddressFromPractices,
  transformAddressForSearchToString,
  transformHomeCareAddressToString
} from '../../../../../utils/address'
import { TEST_IDS } from '../../../../../__tests__/testUtils/constants'
import { useVisibilityComponents } from '../../../../../hooks/useVisibilityComponents'
import SearchAppointmentModal from '../../../../search/SearchAppointmentModal'
import { getDefaultCountry } from '../../../../../constants/global'
import {
  SearchProfileCardContainer,
  StyledDivider,
  RatePaperContainer,
  ContactContainer,
  ProfileTitleContainer
} from './styles'
import { getTreatments } from '../../../../../utils/getTreatments'
import { getLinkReviewInfo } from '../../../../../utils/getLinkReviewInfo'
import ProfileBadges from './ProfileBadges'
import ProfileAvatar from './ProfileAvatar'
import ProfileTitle from './ProfileTitle'
import ProfileRating from './ProfileRating'

const SearchProfileCard = ({
  specialist,
  practice,
  handleSetVideoPlayerModalData,
  isSponsor
}: PracticeOrSpecialistProfile) => {
  const {
    state: {
      filters: { selectedKeyword, appointmentType, distance: filterDistance },
      specialty
    }
  } = useContext(SearchProfilesPageContext)
  const visibilityComponents = useVisibilityComponents({ specialist, practice })
  const introductionVideo = (specialist || practice).media?.[0]
  const translate = useTranslateMessage()
  const { language, locale } = useContext(AppDataContext)
  const defaultCountry = getDefaultCountry(locale)
  const router = useRouter()

  const profileData = useGetSearchProfileData({
    ...(practice ? { practice } : { specialist }),
    language,
    specialty
  })

  const {
    profileAvatarLink,
    profileAbbreviation,
    averageRating,
    practices,
    distance,
    isUnclaimed,
    profileFullName,
    profileReviewTotal,
    haveVideoConsultation,
    topLevelKeyword,
    transformedAddress,
    yearsOfExperienceValue,
    isPractice,
    externalId,
    shouldShowContactButton,
    slug
  } = profileData

  const handleOpenIntroductionVideoPlayer = () => {
    window.dataLayer.push({
      event: 'src_pg_prf_vid_click',
      cont_prf_name: profileFullName,
      cont_uuid: externalId,
      cont_prf_type: isPractice ? 'Practice' : 'Specialist'
    })
    handleSetVideoPlayerModalData({
      url: introductionVideo?.link,
      practiceId: practice?.id,
      specialistId: specialist?.id,
      mediaId: introductionVideo?.id
    })
  }

  const getUnitsByLocale = useGetUnitsByLocale()

  const locationString = useMemo(() => {
    const addressToTransform = getAddressFromPractices(practices, true)
    const isOnlyVideoConsultation = practices.length === 1 && practices[0].slug === FilterType.VIDEO_CONSULTATION
    const filterDistanceNumber = Number(filterDistance)

    if (
      appointmentType === AppointmentType.VIDEO_CONSULTATION ||
      isOnlyVideoConsultation ||
      (haveVideoConsultation && filterDistanceNumber < distance)
    ) {
      return translate('videoConsultation.availableOnline')
    }

    if (specialist?.type === 'homecare' && specialist?.address) {
      return `${getUnitsByLocale(specialist.distance, 2).distanceWithUnits} | ${transformHomeCareAddressToString(
        specialist?.address,
        language
      )}`
    }

    if (!addressToTransform) {
      return ''
    }

    return `${getUnitsByLocale(distance, 2).distanceWithUnits} | ${transformAddressForSearchToString(
      addressToTransform,
      language
    )}`
  }, [
    practices,
    appointmentType,
    haveVideoConsultation,
    filterDistance,
    distance,
    specialist?.type,
    specialist?.address,
    language,
    getUnitsByLocale,
    specialist?.distance,
    translate
  ])

  const { mergedStatistic, reviewStatistic } = useStatistic(specialist?.statistic || practice?.statistic || [])

  const treatments = useMemo(
    () =>
      getTreatments({
        specialist,
        practice,
        mergedStatistic,
        reviewStatistic,
        language,
        specialty,
        selectedKeyword
      }),
    [specialist, practice, mergedStatistic, reviewStatistic, language, specialty, selectedKeyword]
  )

  const lengthKeywords = specialist?.statisticLength ?? practice?.statisticLength

  const { link } = getLinkReviewInfo({ practice, specialist, locale })

  const finalTreatments = useMemo(() => {
    if (!lengthKeywords || lengthKeywords <= 0) {
      return treatments
    }
    return [
      ...treatments,
      {
        variant: '',
        label: `+${lengthKeywords}`,
        href: link,
        backgroundColor: '#EAEEFC'
      }
    ]
  }, [treatments, lengthKeywords, link])

  const activeRelatedKeyword = specialty

  const saveActiveKeywordInStorage = () => {
    if (activeRelatedKeyword.id !== 1 && activeRelatedKeyword?.name?.[language]) {
      localStorage.setItem(`activeKeyword-${slug}`, activeRelatedKeyword.name[language])
    }
  }

  const handleOpenProfilePage = useCallback(() => router.push(link), [router, link])

  const handleOpenProfilePageReviews = useCallback(() => router.push(link + '#reviews'), [router, link])

  const location = useMemo(
    () => (practice ? transformedAddress || defaultCountry : locationString),
    [practice, transformedAddress, defaultCountry, locationString]
  )

  const formattedSuffixes = useMemo(() => {
    const tempText = specialist?.suffix?.[language] || ''

    return tempText.split(', ').slice(0, 4).join(', ')
  }, [language, specialist?.suffix])

  const ProfileBadgesComponent = useMemo(
    () => (
      <ProfileBadges
        peerRecommendationsCount={specialist?.peerRecommendationsCount}
        yearsOfExperience={yearsOfExperienceValue}
        treatments={finalTreatments}
        isPractice={isPractice}
        location={location}
      />
    ),
    [specialist?.peerRecommendationsCount, yearsOfExperienceValue, finalTreatments, isPractice, location]
  )

  return (
    <SearchProfileCardContainer
      topBorder={isSponsor}
      onClick={handleOpenProfilePage}
      data-testid={TEST_IDS.SEARCH_RESULT_CARD}
      onMouseDown={saveActiveKeywordInStorage}
      onAuxClick={(e) => {
        if (e.button === 1) {
          saveActiveKeywordInStorage()
          window.open(link, '_blank')
        }
      }}
    >
      <Grid container wrap="nowrap" width="100%" p="16px" gap="16px">
        <ProfileAvatar
          isSponsor={!!isSponsor}
          profileFullName={profileFullName}
          introductionVideo={introductionVideo}
          profileAvatarLink={profileAvatarLink}
          profileAbbreviation={profileAbbreviation}
          handleOpenIntroductionVideoPlayer={handleOpenIntroductionVideoPlayer}
          introductionVideoAvailable={!!visibilityComponents.introductionVideoAvailable}
        />
        <ProfileTitleContainer container>
          <ProfileTitle
            link={link}
            isPractice={isPractice}
            isUnclaimed={isUnclaimed}
            topLevelKeyword={topLevelKeyword}
            profileFullName={profileFullName}
            formattedSuffixes={formattedSuffixes}
            specialistSuffix={specialist?.suffix?.[language]}
            feeAgreed={!!specialist?.customFields?.feeAgreed}
            isPremium={!!practice?.customFields?.premium}
          />
          <Hidden lgDown implementation="css">
            {ProfileBadgesComponent}
          </Hidden>
        </ProfileTitleContainer>
      </Grid>
      <Box component={Hidden} lgUp implementation="css" px="16px">
        <StyledDivider />
      </Box>
      <RatePaperContainer container className="rate-paper-container">
        <ProfileRating
          isPractice={isPractice}
          averageRating={averageRating}
          profileReviewTotal={profileReviewTotal}
          handleOpenProfilePageReviews={handleOpenProfilePageReviews}
          peerRecommendationsCount={specialist?.peerRecommendationsCount}
        />
        <Hidden lgUp implementation="css">
          {ProfileBadgesComponent}
        </Hidden>
        {shouldShowContactButton && (
          <ContactContainer container onClick={(e) => e.stopPropagation()}>
            <StyledDivider />
            <SearchAppointmentModal
              isSearchPage
              practice={practice}
              isPractice={isPractice}
              specialist={specialist}
              externalId={externalId}
              isUnclaimed={isUnclaimed}
              totalReviews={profileReviewTotal}
              profileFullName={profileFullName}
            />
          </ContactContainer>
        )}
      </RatePaperContainer>
    </SearchProfileCardContainer>
  )
}

export default SearchProfileCard
